// One-hour sign-in (Razan, 2026-11-04): a warning five minutes before the end, and after
// the hour a screen that asks the person to sign in again. Nothing is sent to the server
// meanwhile (requireActiveClient refuses), and the server enforces the same hour.
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AccountSignInExpired } from '@/features/account/SignIn'
import { catalogueEntry } from '@/features/demo/personas'
import { isAccountBuild } from '@/lib/env'
import { useSession } from '@/lib/queries'
import { signInAgain, signInRemainingMs } from '@/lib/session'
import { Banner, Button, Card } from './ui'

/**
 * Re-renders every 15 s so the warning and the end appear on time. persona: the demo
 * persona code, or in account builds the signed-in email address (null signed out).
 */
export function useSignInRemaining(): { persona: string | null; remainingMs: number | null } {
  const session = useSession()
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(t)
  }, [])
  if (session.accountMode) {
    if (!session.account) return { persona: null, remainingMs: null }
    return { persona: session.account.email, remainingMs: signInRemainingMs(null, now) }
  }
  if (!session.activePersona) return { persona: null, remainingMs: null }
  return { persona: session.activePersona, remainingMs: signInRemainingMs(session.activePersona, now) }
}

export function SignInWarning({ remainingMs }: { remainingMs: number }) {
  const minutes = Math.max(1, Math.ceil(remainingMs / 60_000))
  return (
    <div className="no-print mb-4" data-testid="sign-in-warning">
      <Banner tone="warn" title={`Your sign-in ends in ${minutes} minute${minutes === 1 ? '' : 's'}`}>
        Save any draft you are working on. After one hour you will need to sign in again.
      </Banner>
    </div>
  )
}

export function SignInExpired({ persona }: { persona: string }) {
  // Account builds: the same person asks for a new emailed code (0022).
  if (isAccountBuild) return <AccountSignInExpired email={persona} />
  return <PersonaSignInExpired persona={persona} />
}

function PersonaSignInExpired({ persona }: { persona: string }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const who = catalogueEntry(persona)?.name ?? persona
  async function again() {
    setBusy(true)
    setError(null)
    try {
      await signInAgain()
      await queryClient.invalidateQueries()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="mx-auto max-w-xl" data-testid="sign-in-expired">
      <Card>
        <h1 className="display text-[25px] leading-tight font-semibold text-ink">Your sign-in has ended</h1>
        <p className="mt-2 text-ink-soft">
          For safety, a sign-in lasts one hour. Sign in again to continue as {who}. Nothing was sent or signed after your
          sign-in ended; a draft keeps what you last saved.
        </p>
        {error ? (
          <Banner tone="bad" className="mt-3" title="Could not sign in again">
            {error}
          </Banner>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={again} loading={busy} data-testid="sign-in-again">
            Sign in again
          </Button>
          <Link to="/demo" className="tap inline-flex items-center text-sm font-semibold text-brand-dark underline">
            Use a different persona
          </Link>
        </div>
        <p className="mt-3 text-[13px] text-ink-faint">
          Synthetic demonstration: signing in again is one step for the same fictional persona. A real deployment asks for
          your credentials here.
        </p>
      </Card>
    </div>
  )
}
